// CATALOG MANAGER — non-technical CMS for the medication-first architecture.
// Master list (left) · Editor drawer (right). One save persists every field.
// Owner can: Add · Edit · Delete · Pause/Resume · Duplicate · Reorder
// Fields: name, slug, category, prices, images, copy, benefits, strengths, FAQ, SEO.
// Every change here propagates instantly to /best/:slug, the homepage Medication Zone,
// the sitemap, and any future surface that reads the catalog.
import { useCallback, useEffect, useState } from "react";
import {
  Pill, Plus, Trash2, Copy as CopyIcon, Eye, EyeOff, ExternalLink, Save,
  ChevronUp, ChevronDown, GripVertical, Image as ImageIcon, Search, Sparkles,
} from "lucide-react";
import { API, RED, NEON, authHeader } from "./shared";

const CATEGORIES = [
  { slug: "weight-loss",   label: "Weight Loss" },
  { slug: "mens-health",   label: "Men's Health" },
  { slug: "womens-health", label: "Women's Health" },
];

const blankMed = () => ({
  slug: "",
  name: "",
  category: "weight-loss",
  short_description: "",
  description: "",
  benefits: [],
  benefit_cards: [],
  faq: [],
  strengths: [],
  starting_price_cents: 0,
  full_price_cents: 0,
  promo_price_cents: null,
  image_url: "",
  cap_color: "#FF2A3C",
  active: true,
  order: 999,
  seo_title: "",
  seo_description: "",
  og_image: "",
  related_slugs: [],
});

export function CatalogManager({ token }) {
  const [meds, setMeds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // medication being edited or null
  const [isNew, setIsNew] = useState(false);
  const [filter, setFilter] = useState("");
  const [catFilter, setCatFilter] = useState("all");

  const load = useCallback(() => {
    setLoading(true);
    fetch(`${API}/catalog/medications?include_inactive=true`)
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => setMeds(d || []))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = meds.filter((m) => {
    if (catFilter !== "all" && m.category !== catFilter) return false;
    if (filter && !`${m.name} ${m.slug}`.toLowerCase().includes(filter.toLowerCase())) return false;
    return true;
  });

  function startEdit(m) { setEditing({ ...m }); setIsNew(false); }
  function startNew() { setEditing(blankMed()); setIsNew(true); }
  function closeEditor() { setEditing(null); setIsNew(false); }

  async function save(med) {
    const url = isNew
      ? `${API}/catalog/medications`
      : `${API}/catalog/medications/${med.slug}`;
    const method = isNew ? "POST" : "PATCH";
    const body = isNew
      ? med
      : Object.fromEntries(Object.entries(med).filter(([k]) =>
          !["id", "created_at", "updated_at", "slug"].includes(k)
        ));
    const res = await fetch(url, {
      method,
      headers: { "Content-Type": "application/json", ...authHeader(token) },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: "Save failed" }));
      alert(`Save failed: ${err.detail || res.status}`);
      return;
    }
    await load();
    closeEditor();
  }

  async function remove(slug) {
    if (!confirm(`Delete "${slug}"? This cannot be undone.`)) return;
    await fetch(`${API}/catalog/medications/${slug}`, {
      method: "DELETE", headers: authHeader(token),
    });
    load();
  }

  async function togglePause(m) {
    await fetch(`${API}/catalog/medications/${m.slug}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", ...authHeader(token) },
      body: JSON.stringify({ active: !m.active }),
    });
    load();
  }

  async function duplicate(m) {
    const newSlug = prompt(`Duplicate "${m.name}" — new slug?`, `${m.slug}-copy`);
    if (!newSlug) return;
    const res = await fetch(`${API}/catalog/medications/${m.slug}/duplicate?new_slug=${encodeURIComponent(newSlug)}`, {
      method: "POST",
      headers: authHeader(token),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: "Duplicate failed" }));
      alert(`Duplicate failed: ${err.detail || res.status}`);
      return;
    }
    load();
  }

  return (
    <div className="space-y-4" data-testid="catalog-manager">
      <div className="rounded-sm border border-white/10 bg-black/50">
        <header className="px-5 py-3 border-b border-white/10 flex items-center justify-between flex-wrap gap-2">
          <p className="font-heavy text-[14px] inline-flex items-center gap-2">
            <Pill size={14} color={RED} /> MEDICATION CATALOG · {meds.length} ITEMS
          </p>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-white/45" />
              <input
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="Search name or slug…"
                data-testid="catalog-search"
                className="bg-black border border-white/15 pl-7 pr-3 py-1.5 text-white text-[11.5px] font-mono w-56 focus:border-[#FF2A3C] focus:outline-none"
              />
            </div>
            <select
              value={catFilter}
              onChange={(e) => setCatFilter(e.target.value)}
              data-testid="catalog-cat-filter"
              className="bg-black border border-white/15 px-2 py-1.5 text-white text-[11.5px] font-mono focus:border-[#FF2A3C] focus:outline-none"
            >
              <option value="all">All Categories</option>
              {CATEGORIES.map((c) => (
                <option key={c.slug} value={c.slug}>{c.label}</option>
              ))}
            </select>
            <button
              onClick={startNew}
              data-testid="catalog-add-btn"
              className="inline-flex items-center gap-1 px-3 py-1.5 text-[11px] font-extrabold tracking-[0.12em] uppercase border"
              style={{ background: RED, borderColor: RED, color: "white" }}
            >
              <Plus size={12} /> Add Medication
            </button>
          </div>
        </header>

        {/* Table */}
        <div className="divide-y divide-white/5 max-h-[640px] overflow-y-auto">
          {loading && <p className="text-center text-white/45 text-[12px] py-10">Loading catalog…</p>}
          {!loading && filtered.length === 0 && (
            <p className="text-center text-white/45 text-[12px] py-10" data-testid="catalog-empty">
              No medications match — try a different filter.
            </p>
          )}
          {filtered.map((m) => (
            <Row key={m.slug} m={m}
              onEdit={() => startEdit(m)}
              onPause={() => togglePause(m)}
              onDuplicate={() => duplicate(m)}
              onDelete={() => remove(m.slug)}
            />
          ))}
        </div>
      </div>

      {editing && (
        <Editor
          med={editing}
          isNew={isNew}
          token={token}
          onChange={(patch) => setEditing((e) => ({ ...e, ...patch }))}
          onSave={() => save(editing)}
          onClose={closeEditor}
        />
      )}
    </div>
  );
}

/* ─────────────  ROW  ───────────── */
function Row({ m, onEdit, onPause, onDuplicate, onDelete }) {
  const price = `$${Math.round((m.starting_price_cents || 0) / 100)}`;
  return (
    <div
      className="grid grid-cols-12 gap-2 px-3 sm:px-5 py-2.5 items-center hover:bg-white/[0.02]"
      data-testid={`catalog-row-${m.slug}`}
    >
      <div className="col-span-1 hidden sm:flex">
        <div className="w-9 h-9 rounded-sm bg-white/5 border border-white/10 overflow-hidden flex items-center justify-center">
          {m.image_url ? (
            <img src={m.image_url} alt={m.name} className="w-full h-full object-contain" />
          ) : (
            <ImageIcon size={14} className="text-white/35" />
          )}
        </div>
      </div>
      <button
        onClick={onEdit}
        data-testid={`catalog-edit-${m.slug}`}
        className="col-span-7 sm:col-span-5 text-left"
      >
        <p className="font-heavy text-[13px] tracking-[0.06em] text-white truncate">{m.name}</p>
        <p className="text-[10.5px] font-mono text-white/50 truncate">/best/{m.slug}</p>
      </button>
      <div className="col-span-2 hidden sm:block">
        <span className="text-[10px] tracking-[0.16em] font-extrabold uppercase text-white/55">
          {(CATEGORIES.find((c) => c.slug === m.category) || {}).label || m.category}
        </span>
      </div>
      <div className="col-span-2 hidden sm:block text-right">
        <span className="font-heavy text-[13px]" style={{ color: m.promo_price_cents ? RED : "white" }}>
          {m.promo_price_cents
            ? `$${Math.round(m.promo_price_cents / 100)}`
            : price}
        </span>
        {m.promo_price_cents && (
          <span className="ml-1 text-[9.5px] line-through text-white/35">{price}</span>
        )}
      </div>
      <div className="col-span-5 sm:col-span-2 flex justify-end gap-1">
        <a href={`/best/${m.slug}`} target="_blank" rel="noreferrer"
          data-testid={`catalog-view-${m.slug}`}
          title="View public page"
          className="p-1.5 text-white/55 hover:text-white">
          <ExternalLink size={13} />
        </a>
        <button onClick={onPause}
          data-testid={`catalog-pause-${m.slug}`}
          title={m.active ? "Pause" : "Resume"}
          className="p-1.5">
          {m.active
            ? <Eye size={13} color={NEON} />
            : <EyeOff size={13} color="rgba(255,255,255,0.4)" />}
        </button>
        <button onClick={onDuplicate}
          data-testid={`catalog-dup-${m.slug}`}
          title="Duplicate" className="p-1.5 text-white/55 hover:text-white">
          <CopyIcon size={13} />
        </button>
        <button onClick={onDelete}
          data-testid={`catalog-del-${m.slug}`}
          title="Delete" className="p-1.5 text-white/45 hover:text-[#FF2A3C]">
          <Trash2 size={13} />
        </button>
      </div>
    </div>
  );
}

/* ─────────────  EDITOR DRAWER  ───────────── */
function Editor({ med, isNew, onChange, onSave, onClose, token }) {
  const setField = (k) => (v) => onChange({ [k]: v });

  // Single-slot regeneration helper used by the Image Library "Regenerate" buttons.
  // Updates the editor state immediately so the new image previews without needing to save.
  async function regenSlot(slug, slot, onUrl) {
    const r = await fetch(`${API}/catalog/medications/${slug}/generate-image?slot=${slot}`, {
      method: "POST", headers: authHeader(token),
    });
    if (!r.ok) {
      const err = await r.json().catch(() => ({ detail: "Generation failed" }));
      alert(`Generation failed: ${err.detail || r.status}`);
      return;
    }
    const { url } = await r.json();
    // Cache-bust so the <img> reloads even when the path stays the same.
    onUrl(`${url}?t=${Date.now()}`);
  }

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="catalog-editor">
      <button
        aria-label="Close editor"
        onClick={onClose}
        className="flex-1 bg-black/60 backdrop-blur-sm"
      />
      <aside
        className="w-full max-w-[680px] bg-[#0A0B10] border-l border-white/10 flex flex-col"
        role="dialog"
      >
        <header className="px-5 py-4 border-b border-white/10 flex items-center justify-between flex-shrink-0">
          <div>
            <p className="text-[10px] tracking-[0.18em] font-bold text-white/55">
              {isNew ? "NEW MEDICATION" : "EDIT MEDICATION"}
            </p>
            <p className="font-heavy text-white text-[18px] tracking-tight">{med.name || "—"}</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onClose} data-testid="catalog-editor-cancel"
              className="px-3 py-1.5 text-[11px] font-extrabold tracking-[0.14em] uppercase border border-white/20 text-white/70 hover:text-white">
              Cancel
            </button>
            <button onClick={onSave} data-testid="catalog-editor-save"
              className="inline-flex items-center gap-1 px-4 py-1.5 text-[11px] font-extrabold tracking-[0.14em] uppercase"
              style={{ background: RED, color: "white" }}>
              <Save size={12} /> Save
            </button>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5">
          {/* BASIC */}
          <Section title="Basic">
            <Field label="Name *">
              <input value={med.name} onChange={(e) => setField("name")(e.target.value)}
                data-testid="catalog-field-name" className={inputCls()} />
            </Field>
            <Field label={isNew ? "Slug * (URL part)" : "Slug (read-only)"}>
              <input value={med.slug} disabled={!isNew}
                onChange={(e) => setField("slug")(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"))}
                data-testid="catalog-field-slug"
                className={inputCls() + (isNew ? "" : " opacity-60")} />
              {isNew && <p className="text-[10px] text-white/40 mt-1">URL: /best/{med.slug || "your-slug"}</p>}
            </Field>
            <Field label="Category">
              <select value={med.category} onChange={(e) => setField("category")(e.target.value)}
                data-testid="catalog-field-category" className={inputCls()}>
                {CATEGORIES.map((c) => <option key={c.slug} value={c.slug}>{c.label}</option>)}
              </select>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Cap Color (vial)">
                <div className="flex gap-2 items-center">
                  <input type="color" value={med.cap_color || "#FF2A3C"}
                    onChange={(e) => setField("cap_color")(e.target.value)}
                    data-testid="catalog-field-cap-color"
                    className="w-10 h-9 bg-transparent border border-white/15 rounded-sm cursor-pointer p-0.5" />
                  <input value={med.cap_color || ""} onChange={(e) => setField("cap_color")(e.target.value)}
                    className={inputCls()} />
                </div>
              </Field>
              <Field label="Display Order">
                <input type="number" value={med.order ?? 0}
                  onChange={(e) => setField("order")(parseInt(e.target.value || "0", 10))}
                  data-testid="catalog-field-order" className={inputCls()} />
              </Field>
            </div>
            <Field label="">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={med.active}
                  onChange={(e) => setField("active")(e.target.checked)}
                  data-testid="catalog-field-active"
                  className="accent-[#FF2A3C] w-4 h-4" />
                <span className="text-[12px] font-extrabold tracking-[0.10em] uppercase text-white">
                  Active (show on public site)
                </span>
              </label>
            </Field>
          </Section>

          {/* PRICING */}
          <Section title="Pricing">
            <div className="grid grid-cols-3 gap-3">
              <PriceField label="Starting / mo"
                cents={med.starting_price_cents}
                onChange={(c) => setField("starting_price_cents")(c)}
                testid="catalog-price-start" />
              <PriceField label="Full / mo"
                cents={med.full_price_cents}
                onChange={(c) => setField("full_price_cents")(c)}
                testid="catalog-price-full" />
              <PriceField label="Promo (optional)"
                cents={med.promo_price_cents}
                onChange={(c) => setField("promo_price_cents")(c)}
                allowNull
                testid="catalog-price-promo" />
            </div>
            <p className="text-[10.5px] text-white/40">
              Tip: Promo price (if set) overrides the starting price on the public site
              and displays a red <span style={{ color: RED, fontWeight: 800 }}>PROMO</span> badge.
            </p>
          </Section>

          {/* COPY */}
          <Section title="Copy">
            <Field label="Short Description (hero subhead)">
              <textarea value={med.short_description || ""}
                onChange={(e) => setField("short_description")(e.target.value)}
                rows={2}
                data-testid="catalog-field-short"
                className={inputCls()} />
            </Field>
            <Field label="Long Description (What Is This?)">
              <textarea value={med.description || ""}
                onChange={(e) => setField("description")(e.target.value)}
                rows={5}
                data-testid="catalog-field-long"
                className={inputCls()} />
            </Field>
          </Section>

          {/* IMAGE LIBRARY — per-medication visual identity */}
          <Section title="Image Library — Per Medication">
            <p className="text-[10.5px] text-white/45 -mt-1">
              Each medication has its own image set. Change any image here and it updates
              everywhere on the site (homepage card, category page, /best/:slug hero, assessment, social share).
            </p>
            {!isNew && <GenerateAllButton slug={med.slug} token={token} onDone={(patch) => onChange(patch)} />}
            <ImageField
              label="Hero Image"
              hint="Large hero on /best/:slug. Landscape or square works."
              value={med.hero_image || ""}
              onChange={setField("hero_image")}
              onRegenerate={isNew ? null : () => regenSlot(med.slug, "hero", (url) => setField("hero_image")(url))}
              testid="catalog-img-hero" />
            <ImageField
              label="Product Image"
              hint="Vial, tablet, syringe — the actual medication close-up."
              value={med.product_image || ""}
              onChange={setField("product_image")}
              onRegenerate={isNew ? null : () => regenSlot(med.slug, "product", (url) => setField("product_image")(url))}
              testid="catalog-img-product" />
            <ImageField
              label="Card Image"
              hint="Used on the homepage Medication Zone tiles and category cards."
              value={med.card_image || ""}
              onChange={setField("card_image")}
              onRegenerate={isNew ? null : () => regenSlot(med.slug, "card", (url) => setField("card_image")(url))}
              testid="catalog-img-card" />
            <ImageField
              label="Assessment Image"
              hint="Shown in the assessment flow so the form feels built for this medication."
              value={med.assessment_image || ""}
              onChange={setField("assessment_image")}
              onRegenerate={isNew ? null : () => regenSlot(med.slug, "assessment", (url) => setField("assessment_image")(url))}
              testid="catalog-img-assessment" />
            <ImageField
              label="Social Share Image"
              hint="Open Graph image — appears when someone shares the page on Facebook, X, iMessage."
              value={med.social_image || ""}
              onChange={setField("social_image")}
              onRegenerate={isNew ? null : () => regenSlot(med.slug, "social", (url) => setField("social_image")(url))}
              testid="catalog-img-social" />
            <Field label="Gallery Images (extra photos shown as a grid)">
              <ListEditor
                items={med.gallery_images || []}
                onChange={(arr) => setField("gallery_images")(arr)}
                renderItem={(v, set) => (
                  <div className="flex gap-2 items-center">
                    <div className="w-14 h-14 rounded-sm bg-white/5 border border-white/10 overflow-hidden flex-shrink-0 flex items-center justify-center">
                      {v ? (
                        <img src={v} alt="" className="w-full h-full object-cover"
                          onError={(e) => { e.currentTarget.style.display = "none"; }} />
                      ) : <ImageIcon size={14} className="text-white/35" />}
                    </div>
                    <input value={v || ""} onChange={(e) => set(e.target.value)}
                      placeholder="https://… or /assets/meds/your-image.png"
                      className={inputCls()} />
                  </div>
                )}
                addLabel="+ Add Gallery Image"
                testid="catalog-gallery"
                blank={() => ""}
              />
            </Field>
            <details className="pt-2">
              <summary className="text-[10.5px] tracking-[0.14em] font-bold text-white/45 uppercase cursor-pointer hover:text-white/70">
                Legacy image_url (fallback)
              </summary>
              <div className="mt-2">
                <Field label="image_url (used when above slots are empty)">
                  <input value={med.image_url || ""} onChange={(e) => setField("image_url")(e.target.value)}
                    data-testid="catalog-field-image"
                    placeholder="/assets/meds/tirzepatide.png"
                    className={inputCls()} />
                </Field>
              </div>
            </details>
          </Section>

          {/* BENEFITS */}
          <Section title="Benefits (short labels)">
            <ListEditor
              items={med.benefits || []}
              onChange={(arr) => setField("benefits")(arr)}
              renderItem={(v, set) => (
                <input value={v} onChange={(e) => set(e.target.value)}
                  placeholder="e.g. Appetite Control"
                  className={inputCls()} />
              )}
              addLabel="+ Add Benefit"
              testid="catalog-benefits"
              blank={() => ""}
            />
          </Section>

          {/* BENEFIT CARDS */}
          <Section title="Benefit Cards (title + description shown on /best/:slug)">
            <ListEditor
              items={med.benefit_cards || []}
              onChange={(arr) => setField("benefit_cards")(arr)}
              renderItem={(v, set) => (
                <div className="space-y-1.5">
                  <input value={v.title || ""}
                    onChange={(e) => set({ ...v, title: e.target.value })}
                    placeholder="Title (e.g. Appetite Control)"
                    className={inputCls()} />
                  <textarea value={v.body || ""}
                    onChange={(e) => set({ ...v, body: e.target.value })}
                    rows={2}
                    placeholder="Description (1-2 sentences)…"
                    className={inputCls()} />
                </div>
              )}
              addLabel="+ Add Benefit Card"
              testid="catalog-benefit-cards"
              blank={() => ({ title: "", body: "" })}
            />
          </Section>

          {/* STRENGTHS */}
          <Section title="Available Strengths (clickable on /best/:slug)">
            <ListEditor
              items={med.strengths || []}
              onChange={(arr) => setField("strengths")(arr)}
              renderItem={(v, set) => (
                <div className="grid grid-cols-12 gap-2 items-center">
                  <input value={v.value || ""}
                    onChange={(e) => set({ ...v, value: e.target.value })}
                    placeholder="2.5"
                    className={inputCls() + " col-span-4"} />
                  <input value={v.unit || "mg"}
                    onChange={(e) => set({ ...v, unit: e.target.value })}
                    placeholder="mg"
                    className={inputCls() + " col-span-3"} />
                  <label className="col-span-5 inline-flex items-center gap-1.5 text-[11px] font-extrabold tracking-[0.10em] uppercase text-white/70">
                    <input type="checkbox" checked={!!v.is_popular}
                      onChange={(e) => set({ ...v, is_popular: e.target.checked })}
                      className="accent-[#FF2A3C] w-3.5 h-3.5" />
                    Mark Popular
                  </label>
                </div>
              )}
              addLabel="+ Add Strength"
              testid="catalog-strengths"
              blank={() => ({ value: "", unit: "mg", is_popular: false })}
            />
          </Section>

          {/* FAQ */}
          <Section title="FAQ">
            <ListEditor
              items={med.faq || []}
              onChange={(arr) => setField("faq")(arr)}
              renderItem={(v, set) => (
                <div className="space-y-1.5">
                  <input value={v.q || ""} onChange={(e) => set({ ...v, q: e.target.value })}
                    placeholder="Question"
                    className={inputCls()} />
                  <textarea value={v.a || ""} onChange={(e) => set({ ...v, a: e.target.value })}
                    rows={2}
                    placeholder="Answer"
                    className={inputCls()} />
                </div>
              )}
              addLabel="+ Add FAQ"
              testid="catalog-faq"
              blank={() => ({ q: "", a: "" })}
            />
          </Section>

          {/* CROSS-SELL — admin-curated "Patients Also Explore" list */}
          <Section title="Cross-Sell — Patients Also Explore">
            <p className="text-[10.5px] text-white/45 -mt-1">
              Pick the medications shown on /best/{med.slug}'s "Patients Also Explore" rail.
              Leave empty to auto-derive from the same category.
            </p>
            <CrossSellPicker
              selected={med.cross_sell_slugs || []}
              currentSlug={med.slug}
              onChange={(arr) => setField("cross_sell_slugs")(arr)}
              token={token}
            />
          </Section>

          {/* SEO */}
          <Section title="SEO">
            <Field label="SEO Title (browser tab + Google)">
              <input value={med.seo_title || ""} onChange={(e) => setField("seo_title")(e.target.value)}
                placeholder={`${med.name} — BestRx`}
                data-testid="catalog-field-seo-title"
                className={inputCls()} />
            </Field>
            <Field label="Meta Description (search result snippet)">
              <textarea value={med.seo_description || ""}
                onChange={(e) => setField("seo_description")(e.target.value)}
                rows={2}
                data-testid="catalog-field-seo-desc"
                className={inputCls()} />
            </Field>
            <Field label="Open Graph Image (social previews)">
              <input value={med.og_image || ""}
                onChange={(e) => setField("og_image")(e.target.value)}
                placeholder="https://… (defaults to image_url)"
                data-testid="catalog-field-og"
                className={inputCls()} />
            </Field>
          </Section>
        </div>
      </aside>
    </div>
  );
}

/* ─────────────  EDITOR HELPERS  ───────────── */
function inputCls() {
  return "w-full bg-black border border-white/15 px-2.5 py-1.5 text-white font-mono text-[12px] focus:border-[#FF2A3C] focus:outline-none";
}
function Section({ title, children }) {
  return (
    <section className="rounded-sm border border-white/10 bg-black/40 p-4 space-y-3">
      <p className="text-[10.5px] tracking-[0.18em] font-extrabold uppercase text-white/55">{title}</p>
      {children}
    </section>
  );
}
function Field({ label, children }) {
  return (
    <div>
      {label && <p className="text-[10px] tracking-[0.14em] font-bold text-white/45 mb-1 uppercase">{label}</p>}
      {children}
    </div>
  );
}
function PriceField({ label, cents, onChange, allowNull = false, testid }) {
  const [str, setStr] = useState(cents == null ? "" : (cents / 100).toString());
  useEffect(() => { setStr(cents == null ? "" : (cents / 100).toString()); }, [cents]);
  return (
    <Field label={label}>
      <div className="flex items-center gap-1">
        <span className="text-white/55 font-mono text-[12px]">$</span>
        <input
          value={str}
          onChange={(e) => {
            const v = e.target.value.replace(/[^0-9.]/g, "");
            setStr(v);
            if (v === "") onChange(allowNull ? null : 0);
            else onChange(Math.round(parseFloat(v) * 100));
          }}
          placeholder={allowNull ? "(no promo)" : "0"}
          data-testid={testid}
          className={inputCls()}
        />
      </div>
    </Field>
  );
}

/* Image field — URL input + thumbnail preview + helper hint + optional regenerate button.
   Used 5× in the Image Library section (hero, product, card, assessment, social). */
function ImageField({ label, hint, value, onChange, onRegenerate, testid }) {
  const [regenerating, setRegenerating] = useState(false);
  async function handleRegen() {
    if (!onRegenerate) return;
    setRegenerating(true);
    try { await onRegenerate(); } finally { setRegenerating(false); }
  }
  return (
    <Field label={label}>
      {hint && <p className="text-[10px] text-white/40 -mt-0.5 mb-1.5 italic">{hint}</p>}
      <div className="flex gap-2 items-start">
        <div className="w-20 h-20 rounded-sm bg-white/5 border border-white/15 overflow-hidden flex-shrink-0 flex items-center justify-center">
          {value ? (
            <img src={value} alt={label} className="w-full h-full object-cover"
              onError={(e) => { e.currentTarget.style.display = "none"; }} />
          ) : (
            <ImageIcon size={18} className="text-white/30" />
          )}
        </div>
        <div className="flex-1 min-w-0 space-y-1.5">
          <input
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder="https://… or /assets/meds/your-image.png"
            data-testid={testid}
            className={inputCls()}
          />
          {onRegenerate && (
            <button
              type="button"
              onClick={handleRegen}
              disabled={regenerating}
              data-testid={`${testid}-regen`}
              className="inline-flex items-center gap-1 px-2.5 py-1 text-[10px] font-extrabold tracking-[0.14em] uppercase border disabled:opacity-50"
              style={{ borderColor: RED, color: RED }}
            >
              <Sparkles size={10} /> {regenerating ? "Generating…" : "Regenerate w/ AI"}
            </button>
          )}
        </div>
      </div>
    </Field>
  );
}

/* "Generate Full Set" — calls the all-slots endpoint and updates the editor state
   with every returned URL in one shot. Shows a progress overlay because Nano Banana
   can take 30–60s for 5 parallel generations. */
function GenerateAllButton({ slug, token, onDone }) {
  const [busy, setBusy] = useState(false);
  async function generate() {
    if (!confirm(`Generate a full new image set (5 images) for "${slug}"?\n\nThis uses AI and can take 30–60 seconds. Existing images will be replaced.`)) return;
    setBusy(true);
    try {
      const r = await fetch(`${API}/catalog/medications/${slug}/generate-images`, {
        method: "POST",
        headers: authHeader(token),
      });
      const data = await r.json();
      if (!r.ok) {
        alert(`Generation failed: ${data.detail || r.status}`);
        return;
      }
      const patch = {};
      const stamp = Date.now();
      for (const [slot, url] of Object.entries(data.generated || {})) {
        patch[`${slot}_image`] = `${url}?t=${stamp}`;
      }
      if (Object.keys(data.errors || {}).length) {
        const errLines = Object.entries(data.errors).map(([s, e]) => `• ${s}: ${e}`).join("\n");
        alert(`Generated ${data.success_count}/${data.total} images.\nFailed slots:\n${errLines}`);
      }
      onDone(patch);
    } finally { setBusy(false); }
  }
  return (
    <button type="button" onClick={generate} disabled={busy}
      data-testid="catalog-imggen-all"
      className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 text-[12px] font-extrabold tracking-[0.14em] uppercase border-2 disabled:opacity-60"
      style={{ borderColor: RED, color: "white", background: busy ? "transparent" : `${RED}22` }}>
      <Sparkles size={14} color={RED} />
      {busy ? "Generating 5 AI images… (30-60s)" : "Generate Full Image Set With AI"}
    </button>
  );
}

// (GenerateAllButton receives token directly via props — passed down from CommandCenter → CatalogManager → Editor.)

/* Cross-Sell Picker — multi-select chips listing every active medication.
   Click adds/removes from the cross_sell_slugs array on the medication record.
   The same list (in this order) drives the "Patients Also Explore" rail. */
function CrossSellPicker({ selected, currentSlug, onChange }) {
  const [catalog, setCatalog] = useState([]);
  useEffect(() => {
    fetch(`${API}/catalog/medications?include_inactive=true`)
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => setCatalog(d || []));
  }, []);
  const toggle = (slug) => {
    const next = selected.includes(slug)
      ? selected.filter((s) => s !== slug)
      : [...selected, slug];
    onChange(next);
  };
  const others = catalog.filter((m) => m.slug !== currentSlug);
  return (
    <div className="space-y-2" data-testid="catalog-cross-sell">
      <p className="text-[10.5px] text-white/60 font-bold tracking-[0.12em] uppercase">
        Selected ({selected.length})
      </p>
      <div className="flex flex-wrap gap-1.5 min-h-[28px]">
        {selected.length === 0 && (
          <span className="text-[10.5px] italic text-white/40">None — will auto-derive from category.</span>
        )}
        {selected.map((s) => {
          const m = others.find((x) => x.slug === s);
          return (
            <button key={s} onClick={() => toggle(s)} type="button"
              data-testid={`catalog-cross-sell-selected-${s}`}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-extrabold tracking-[0.10em] uppercase border"
              style={{ background: `${RED}22`, borderColor: RED, color: "white" }}>
              {(m?.name || s)} <span className="text-white/70">×</span>
            </button>
          );
        })}
      </div>
      <p className="text-[10.5px] text-white/60 font-bold tracking-[0.12em] uppercase pt-2">
        Available
      </p>
      <div className="flex flex-wrap gap-1.5">
        {others
          .filter((m) => !selected.includes(m.slug))
          .map((m) => (
            <button key={m.slug} onClick={() => toggle(m.slug)} type="button"
              data-testid={`catalog-cross-sell-pick-${m.slug}`}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-bold tracking-[0.10em] uppercase border border-white/15 text-white/70 hover:text-white hover:border-white/30">
              <Plus size={9} /> {m.name}
            </button>
          ))}
      </div>
    </div>
  );
}

/* Generic list editor — reorder + add + remove for primitives or shaped objects. */
function ListEditor({ items, onChange, renderItem, addLabel, testid, blank }) {
  const set = (i, v) => {
    const arr = [...items];
    arr[i] = v;
    onChange(arr);
  };
  const add = () => onChange([...(items || []), blank()]);
  const del = (i) => onChange(items.filter((_, idx) => idx !== i));
  const move = (i, d) => {
    const j = i + d;
    if (j < 0 || j >= items.length) return;
    const arr = [...items];
    [arr[i], arr[j]] = [arr[j], arr[i]];
    onChange(arr);
  };
  return (
    <div className="space-y-2" data-testid={testid}>
      {(items || []).length === 0 && (
        <p className="text-[11px] text-white/40 italic">Nothing yet. Click the button below.</p>
      )}
      {items.map((v, i) => (
        <div key={i} className="rounded-sm border border-white/10 bg-black/50 p-2.5 flex gap-2 items-start"
          data-testid={`${testid}-item-${i}`}>
          <GripVertical size={12} className="text-white/25 mt-1.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">{renderItem(v, (nv) => set(i, nv))}</div>
          <div className="flex flex-col gap-0.5 flex-shrink-0">
            <button onClick={() => move(i, -1)} disabled={i === 0}
              className="text-white/55 disabled:opacity-25 hover:text-white"><ChevronUp size={12} /></button>
            <button onClick={() => move(i, +1)} disabled={i === items.length - 1}
              className="text-white/55 disabled:opacity-25 hover:text-white"><ChevronDown size={12} /></button>
            <button onClick={() => del(i)}
              data-testid={`${testid}-del-${i}`}
              className="text-white/45 hover:text-[#FF2A3C]"><Trash2 size={12} /></button>
          </div>
        </div>
      ))}
      <button onClick={add}
        data-testid={`${testid}-add`}
        className="w-full inline-flex items-center justify-center gap-1 px-3 py-1.5 text-[11px] font-extrabold tracking-[0.12em] uppercase border border-dashed border-white/20 text-white/70 hover:text-white hover:border-white/40">
        <Plus size={11} /> {addLabel}
      </button>
    </div>
  );
}
